import React, { useState, useEffect, useMemo } from "react";
import { Admin, Resource, Loading, Layout, Menu } from "react-admin";
import { useParams } from "react-router-dom";

import simpleRestProvider from "ra-data-simple-rest";

import RouterIcon from "@mui/icons-material//Router";
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import TimelineIcon from "@mui/icons-material/Timeline";
import BackupIcon from "@mui/icons-material/Backup";
import PolylineIcon from "@mui/icons-material/Polyline";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import WaterIcon from "@mui/icons-material/Water";
import Smartphone from "@mui/icons-material/Smartphone";
import StarsIcon from "@mui/icons-material/Stars";
import BubblesIcon from "@mui/icons-material/BubbleChart";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import ScienceIcon from "@mui/icons-material/Science";
import GroupWorkIcon from "@mui/icons-material/GroupWork";

import {
  BarrelIcon,
  BatchIcon,
  VirtualCompositeIcon,
  SulfateAdditionIcon,
} from "./lib/icons";

import { fetchJson } from "./lib/httpClient";
import { authProvider, hasScope, SCOPE, setScopesCustomerID } from "./lib/auth";
import { theme } from "./lib/theme";
import { CustomAppBar } from "./lib/appLayout";
import { MenuHeading } from "./lib/menuHeading";

import {
  BarrelList,
  BarrelEdit,
  BarrelCreate,
  BarrelShow,
} from "./resources/barrels";
import { FSO2List, FSO2Create, FSO2Edit, FSO2Show } from "./resources/fso2";
import { LotList, LotCreate, LotShow, LotEdit } from "./resources/lots";
import {
  BlendList,
  BlendEdit,
  BlendCreate,
  BlendShow,
} from "./resources/blends";
import {
  SmartTankList,
  SmartTankCreate,
  SmartTankEdit,
  SmartTankShow,
} from "./resources/smart_tanks";
import {
  FermentList,
  FermentCreate,
  FermentShow,
  FermentEdit,
} from "./resources/ferments";
import {
  AutomationProtocolList,
  AutomationProtocolCreate,
  AutomationProtocolEdit,
} from "./resources/automation_protocols";
import { LotVesselEdit } from "./resources/lot_vessel";
import {
  VirtualCompositeList,
  VirtualCompositeShow,
  VirtualCompositeCreate,
} from "./resources/virtual_composites";
import {
  AdditionList,
  AdditionEdit,
  AdditionCreate,
  AdditionShow,
} from "./resources/sulfate_add";
import { LotAdditionList, LotAdditionShow } from "./resources/lot_add";
import Dashboard from "./resources/dashboard";
import {
  DocumentList,
  DocumentCreate,
  DocumentShow,
} from "./resources/documents";
import {
  WaterUseList,
  WaterUseEdit,
  WaterUseShow,
} from "./resources/water_use";
import { AppInstanceList } from "./resources/app_instance";
import { TaskList, TaskCreate, TaskShow } from "./resources/tasks";
import { DeviceList, DeviceEdit } from "./resources/device";
import {
  AnalysisList,
  AnalysisEdit,
  AnalysisCreate,
  AnalysisShow,
} from "./resources/analyses";
import {
  AnalysisTypeList,
  AnalysisTypeEdit,
  AnalysisTypeCreate,
  AnalysisTypeShow,
} from "./resources/analysis_type";
import {
  VintraceTransactionList,
  VintraceTransactionShow,
} from "./resources/vintrace_transactions";

import { setCustInfo } from "./lib/customer";

const convertFileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(file.rawFile);
  });

// Wraps the data provider so that document uploads are converted to base64
const CustomDataProvider = (dataProvider) => {
  return {
    ...dataProvider,
    create: async (resource, params) => {
      if (resource === "documents") {
        params.data.title = params.data.attachment.title;
        params.data.type = params.data.attachment.rawFile.type;
        params.data.size = params.data.attachment.rawFile.size;
        const b64file = await convertFileToBase64(params.data.attachment);
        params.data.attachment = b64file;
      }
      return await dataProvider.create(resource, params);
    },
  };
};

const CustomMenu = () => (
  <Menu>
    <Menu.DashboardItem />
    <MenuHeading label="Wine Locations" />
    <Menu.ResourceItem name="vessels" />
    <Menu.ResourceItem name="lots" />
    <Menu.ResourceItem name="blends" />
    <MenuHeading label="Fermentation" key="Fermentation_heading" />
    <Menu.ResourceItem name="smart_tanks" key="smart_tanks" />
    <Menu.ResourceItem name="ferments" key="ferments" />
    <Menu.ResourceItem name="automation_protocols" key="automation_protocols" />
    <Menu.ResourceItem name="analyses" />
    <Menu.ResourceItem name="analysis_types" />
    <MenuHeading label="SO2 Management" />
    <Menu.ResourceItem name="events/fso2_reading" />
    <Menu.ResourceItem name="virtual_composite" />
    <Menu.ResourceItem name="events/lot_addition" />
    {hasScope(SCOPE.TECHNICIAN)
      ? [
          <MenuHeading label="Resource Usage" key="Resource_Usage_heading" />,
          <Menu.ResourceItem name="water_use" key="water_use" />,
        ]
      : []}
    {hasScope(SCOPE.TECHNICIAN)
      ? [
          <MenuHeading label="Advanced" key="Advanced_heading" />,
          <Menu.ResourceItem name="devices" key="devices" />,
        ]
      : []}
    {hasScope(SCOPE.ADMIN)
      ? [
          <Menu.ResourceItem name="documents" key="documents" />,
          <Menu.ResourceItem name="tasks" key="tasks" />,
          <Menu.ResourceItem name="appinstance" key="appinstance" />,
          <Menu.ResourceItem
            name="vintrace_transactions"
            key="vintrace_transactions"
          />,
        ]
      : []}
  </Menu>
);

const CustomLayout = (props) => (
  <Layout {...props} appBar={CustomAppBar} menu={CustomMenu} />
);

export const CustomerApp = () => {
  const { custId } = useParams();
  const [loading, setLoading] = useState(true);

  const dataProvider = useMemo(() => {
    return CustomDataProvider(
      simpleRestProvider("/api/v2/c/" + custId, fetchJson),
    );
  }, [custId]);

  useEffect(() => {
    console.log(`Fetching customer id=${custId}`);
    fetchJson("/api/v2/c/" + custId).then(async (resp) => {
      // Set some global state for this customer
      setCustInfo(resp.json);
      setScopesCustomerID(custId);
      document.title = "BarrelWise - " + resp.json.customerLongName;

      setLoading(false);
    });
  }, [custId]);

  if (loading) {
    return (
      <Loading
        loadingPrimary="Loading"
        loadingSecondary="Please wait a moment"
      />
    );
  }

  return (
    <Admin
      dataProvider={dataProvider}
      basename={"/c/" + custId}
      authProvider={authProvider}
      layout={CustomLayout}
      theme={theme}
      darkTheme={null}
      loginPage={() => null}
      dashboard={Dashboard}
    >
      <Resource
        name="vessels"
        options={{ label: "Vessels" }}
        list={BarrelList}
        create={hasScope(SCOPE.MANAGER) ? BarrelCreate : undefined}
        edit={hasScope(SCOPE.MANAGER) ? BarrelEdit : undefined}
        show={BarrelShow}
        icon={BarrelIcon}
      />
      <Resource
        name="lots"
        options={{ label: "Lots" }}
        list={LotList}
        create={hasScope(SCOPE.MANAGER) ? LotCreate : undefined}
        edit={hasScope(SCOPE.MANAGER) ? LotEdit : undefined}
        show={LotShow}
        icon={BatchIcon}
      />
      <Resource
        name="blends"
        options={{ label: "Blends" }}
        list={BlendList}
        create={hasScope(SCOPE.MANAGER) ? BlendCreate : undefined}
        edit={hasScope(SCOPE.MANAGER) ? BlendEdit : undefined}
        show={BlendShow}
        icon={InvertColorsIcon}
      />

      <Resource
        name="smart_tanks"
        options={{ label: "Smart Tanks" }}
        list={SmartTankList}
        create={hasScope(SCOPE.ADMIN) ? SmartTankCreate : undefined}
        edit={hasScope(SCOPE.ADMIN) ? SmartTankEdit : undefined}
        show={SmartTankShow}
        icon={StarsIcon}
      />
      <Resource
        name="ferments"
        options={{ label: "Ferments" }}
        list={FermentList}
        create={hasScope(SCOPE.MANAGER) ? FermentCreate : undefined}
        edit={hasScope(SCOPE.ADMIN) ? FermentEdit : undefined}
        show={FermentShow}
        icon={BubblesIcon}
      />

      <Resource
        name="automation_protocols"
        options={{ label: "Protocols" }}
        list={AutomationProtocolList}
        create={hasScope(SCOPE.MANAGER) ? AutomationProtocolCreate : undefined}
        edit={hasScope(SCOPE.MANAGER) ? AutomationProtocolEdit : undefined}
        icon={PlaylistPlayIcon}
      />

      <Resource
        name="events/fso2_reading"
        options={{ label: "Measurements" }}
        list={FSO2List}
        show={FSO2Show}
        create={hasScope(SCOPE.ADMIN) ? FSO2Create : undefined}
        edit={hasScope(SCOPE.ADMIN) ? FSO2Edit : undefined}
        icon={TimelineIcon}
      />
      <Resource
        name="virtual_composite"
        options={{ label: "Virtual Composites" }}
        list={VirtualCompositeList}
        show={VirtualCompositeShow}
        icon={VirtualCompositeIcon}
        create={hasScope(SCOPE.ADMIN) ? VirtualCompositeCreate : undefined}
      />

      <Resource
        name="events/sulfate_addition"
        options={{ label: "Additions" }}
        list={AdditionList}
        create={hasScope(SCOPE.ADMIN) ? AdditionCreate : undefined}
        edit={hasScope(SCOPE.ADMIN) ? AdditionEdit : undefined}
        show={AdditionShow}
        icon={SulfateAdditionIcon}
      />
      <Resource
        name="events/lot_addition"
        options={{ label: "Additions" }}
        list={LotAdditionList}
        show={LotAdditionShow}
        icon={SulfateAdditionIcon}
      />
      <Resource
        name="analyses"
        options={{ label: "Analyses" }}
        list={hasScope(SCOPE.MEMBER) ? AnalysisList : undefined}
        create={hasScope(SCOPE.MANAGER) ? AnalysisCreate : undefined}
        edit={hasScope(SCOPE.MANAGER) ? AnalysisEdit : undefined}
        show={hasScope(SCOPE.MEMBER) ? AnalysisShow : undefined}
        icon={ScienceIcon}
      />
      <Resource
        name="analysis_types"
        options={{ label: "Analysis Types" }}
        list={hasScope(SCOPE.MEMBER) ? AnalysisTypeList : undefined}
        create={hasScope(SCOPE.MANAGER) ? AnalysisTypeCreate : undefined}
        edit={hasScope(SCOPE.MANAGER) ? AnalysisTypeEdit : undefined}
        show={hasScope(SCOPE.MEMBER) ? AnalysisTypeShow : undefined}
        icon={GroupWorkIcon}
      />

      <Resource
        name="water_use"
        options={{ label: "Water Use" }}
        list={hasScope(SCOPE.TECHNICIAN) ? WaterUseList : undefined}
        edit={hasScope(SCOPE.TECHNICIAN) ? WaterUseEdit : undefined}
        show={hasScope(SCOPE.TECHNICIAN) ? WaterUseShow : undefined}
        icon={WaterIcon}
      />
      <Resource
        name="devices"
        list={
          hasScope(SCOPE.TECHNICIAN) ? (
            <DeviceList forCustomer={true} />
          ) : undefined
        }
        edit={
          hasScope(SCOPE.TECHNICIAN) ? (
            <DeviceEdit forCustomer={true} />
          ) : undefined
        }
        icon={RouterIcon}
      />
      <Resource
        name="documents"
        options={{ label: "Documents" }}
        list={hasScope(SCOPE.ADMIN) ? DocumentList : undefined}
        create={hasScope(SCOPE.ADMIN) ? DocumentCreate : undefined}
        show={hasScope(SCOPE.ADMIN) ? DocumentShow : undefined}
        icon={BackupIcon}
      />
      <Resource
        name="tasks"
        options={{ label: "Tasks" }}
        list={hasScope(SCOPE.ADMIN) ? TaskList : undefined}
        create={hasScope(SCOPE.ADMIN) ? TaskCreate : undefined}
        show={hasScope(SCOPE.ADMIN) ? TaskShow : undefined}
        icon={PolylineIcon}
      />
      <Resource
        name="appinstance"
        options={{ label: "App Instances" }}
        list={hasScope(SCOPE.ADMIN) ? AppInstanceList : undefined}
        icon={Smartphone}
      />
      <Resource
        name="vintrace_transactions"
        options={{ label: "Vintrace Transactions" }}
        list={hasScope(SCOPE.ADMIN) ? VintraceTransactionList : undefined}
        show={hasScope(SCOPE.ADMIN) ? VintraceTransactionShow : undefined}
        icon={SwapHoriz}
      />

      {/* Hidden resources that are used for reference fields */}
      <Resource name="barrel_cooper" />
      <Resource name="barrel_toast" />
      <Resource name="barrel_oak" />
      <Resource name="barrel_forest" />
      <Resource name="barrel_type" />
      <Resource
        name="lot_barrel"
        edit={hasScope(SCOPE.ADMIN) ? LotVesselEdit : undefined}
      />
      <Resource name="varietals" />
      <Resource name="events" />
    </Admin>
  );
};
